import React, { useEffect } from 'react';
import { Button, Form, Input, Space, Switch } from 'antd';
import type { FormInstance } from 'antd';
import { ReactComponent as Trash } from 'assets/icons/trash.svg';
import { EIQFormLabel } from 'common/components/label/form-label';
import featureStore from 'common/stores/features/feature.store';
import { observer } from 'mobx-react-lite';
import { ICitationConfigurationFormValues } from './citation-configuration-form.types';
import CitationPenaltyField from './citation-penalty-field';
import './citations-configurations.scss';

interface CitationRuleEscalationTierConfigurationProps {
  index: number;
  ruleName: number;
  name: number;
  restField: Record<string, unknown>;
  form: FormInstance<ICitationConfigurationFormValues>;
  remove: (index: number) => void;
  canRemove: boolean;
  isLast: boolean;
}

const PENALTY_FIELDS = {
  resident: [
    {
      enabledField: 'isResidentFineAmountEnabled',
      valueField: 'residentFineAmount',
      label: 'Resident fine ($)',
      step: 0.1,
    },
    {
      enabledField: 'isResidentSuspendTransponderInDaysEnabled',
      valueField: 'residentSuspendTransponderInDays',
      label: 'Resident Suspend Transponder (days)',
    },
  ],
  visitor: [
    {
      enabledField: 'isVisitorFineAmountEnabled',
      valueField: 'visitorFineAmount',
      label: 'Visitor fine ($)',
      step: 0.1,
    },
    {
      enabledField: 'isVisitorSuspendTransponderInDaysEnabled',
      valueField: 'visitorSuspendTransponderInDays',
      label: 'Visitor Suspend Transponder (days)',
    },
    {
      enabledField: 'isRevokeDrivingPrivilegesInDaysEnabled',
      valueField: 'revokeDrivingPrivilegesInDays',
      label: 'Revoke Driving Privileges (days)',
    },
  ],
} as const;

const PENALTY_ENABLED_FIELDS = Object.values(PENALTY_FIELDS)
  .flat()
  .map(({ enabledField }) => enabledField);

const CitationRuleEscalationTierConfiguration = observer(
  ({ index, ruleName, name, restField, form, remove, canRemove, isLast }: CitationRuleEscalationTierConfigurationProps) => {
    const fieldPath = ['rules', ruleName, 'escalationTiers', name];
    const isWarningOnly = Boolean(Form.useWatch([...fieldPath, 'isWarningOnly'], form));
    const escalationTiers = Form.useWatch(['rules', ruleName, 'escalationTiers'], form);
    const isEscalationTiersEnabled = featureStore.isEIQIntelliTicketEscalationTiersEnabled;

    useEffect(() => {
      const speedFromPath = ['rules', ruleName, 'escalationTiers', name, 'speedFrom'];

      if (!isEscalationTiersEnabled) {
        form.setFieldValue(speedFromPath, null);
        return;
      }

      const previousSpeedTo = index === 0 ? -1 : escalationTiers?.[index - 1]?.speedTo;
      const numericPreviousSpeedTo = Number(previousSpeedTo);
      const nextSpeedFrom = Number.isFinite(numericPreviousSpeedTo) ? numericPreviousSpeedTo + 1 : null;

      if (form.getFieldValue(speedFromPath) !== nextSpeedFrom) {
        form.setFieldValue(speedFromPath, nextSpeedFrom);
      }
    }, [escalationTiers, form, index, isEscalationTiersEnabled, name, ruleName]);

    const handleWarningOnlyChange = (warningOnly: boolean) => {
      if (!warningOnly) {
        return;
      }

      const disabledValues = Object.values(PENALTY_FIELDS)
        .flat()
        .reduce<Record<string, false | null>>((result, field) => {
          result[field.enabledField] = false;
          result[field.valueField] = null;
          return result;
        }, {});

      Object.entries(disabledValues).forEach(([fieldName, value]) => {
        form.setFieldValue([...fieldPath, fieldName], value);
      });
    };

    return (
      <Space className="citation-escalation-tier-wrapper full-width" direction="vertical">
        {isEscalationTiersEnabled && <div className="rule-index">Escalation tier {index + 1}</div>}

        {isEscalationTiersEnabled && (
          <SpeedRangeFields form={form} ruleName={ruleName} tierName={name} restField={restField} isLast={isLast} />
        )}

        <Form.Item
          {...restField}
          className="warning-only-field"
          label={<EIQFormLabel title="Warning only" />}
          name={[name, 'isWarningOnly']}
          valuePropName="checked"
          dependencies={PENALTY_ENABLED_FIELDS.map((fieldName) => [...fieldPath, fieldName])}
          rules={[
            {
              validator: (_, warningOnly) => {
                const hasEnabledPenalty = PENALTY_ENABLED_FIELDS.some(
                  (fieldName) => form.getFieldValue([...fieldPath, fieldName]) === true,
                );

                if (warningOnly || hasEnabledPenalty) {
                  return Promise.resolve();
                }

                return Promise.reject(new Error('Enable Warning only or select at least one penalty'));
              },
            },
          ]}>
          <Switch onChange={handleWarningOnlyChange} />
        </Form.Item>

        <PenaltySection
          title="Resident"
          fields={PENALTY_FIELDS.resident}
          form={form}
          ruleName={ruleName}
          tierName={name}
          isWarningOnly={isWarningOnly}
        />
        <PenaltySection
          title="Visitor"
          fields={PENALTY_FIELDS.visitor}
          form={form}
          ruleName={ruleName}
          tierName={name}
          isWarningOnly={isWarningOnly}
        />

        {canRemove && isEscalationTiersEnabled && (
          <Button type="text" icon={<Trash />} onClick={() => remove(name)}>
            Remove escalation tier
          </Button>
        )}
      </Space>
    );
  },
);

interface PenaltySectionProps {
  title: string;
  fields: readonly {
    enabledField:
      | 'isResidentFineAmountEnabled'
      | 'isResidentSuspendTransponderInDaysEnabled'
      | 'isVisitorFineAmountEnabled'
      | 'isVisitorSuspendTransponderInDaysEnabled'
      | 'isRevokeDrivingPrivilegesInDaysEnabled';
    valueField:
      | 'residentFineAmount'
      | 'residentSuspendTransponderInDays'
      | 'visitorFineAmount'
      | 'visitorSuspendTransponderInDays'
      | 'revokeDrivingPrivilegesInDays';
    label: string;
    step?: number;
  }[];
  form: FormInstance<ICitationConfigurationFormValues>;
  ruleName: number;
  tierName: number;
  isWarningOnly: boolean;
}

const PenaltySection = ({ title, fields, form, ruleName, tierName, isWarningOnly }: PenaltySectionProps) => (
  <div className="resident-section">
    <div className="resident-section-title">{title}</div>
    <div className="resident-section-fields">
      {fields.map((field) => (
        <CitationPenaltyField
          key={field.valueField}
          {...field}
          form={form}
          ruleName={ruleName}
          tierName={tierName}
          isWarningOnly={isWarningOnly}
        />
      ))}
    </div>
  </div>
);

interface SpeedRangeFieldsProps {
  form: FormInstance<ICitationConfigurationFormValues>;
  ruleName: number;
  tierName: number;
  restField: Record<string, unknown>;
  isLast: boolean;
}

const SpeedRangeFields = ({ form, ruleName, tierName, restField, isLast }: SpeedRangeFieldsProps) => (
  <div className="resident-section">
    <div className="resident-section-title">Speed range</div>
    <div className="resident-section-fields citation-escalation-tier-speed-section">
      <div className="resident-section-fields-item">
        <Form.Item
          {...restField}
          label={<EIQFormLabel title="Speed From (mph)" />}
          name={[tierName, 'speedFrom']}
          rules={[{ validator: (_, value) => validateSpeedNumber(value, 'Speed From', true) }]}>
          <Input type="number" min={0} max={300} step={1} disabled />
        </Form.Item>
      </div>
      <div className="resident-section-fields-item">
        <Form.Item
          {...restField}
          label={<EIQFormLabel title="Speed To (mph)" />}
          name={[tierName, 'speedTo']}
          dependencies={[['rules', ruleName, 'escalationTiers', tierName, 'speedFrom']]}
          rules={[
            {
              validator: async (_, value) => {
                await validateSpeedNumber(value, 'Speed To', !isLast);
                if (isEmptyValue(value)) return;

                const speedFrom = form.getFieldValue(['rules', ruleName, 'escalationTiers', tierName, 'speedFrom']);
                if (!isEmptyValue(speedFrom) && Number(value) < Number(speedFrom)) {
                  throw new Error('Speed To must be greater than or equal to Speed From');
                }
              },
            },
          ]}>
          <Input type="number" min={0} max={300} step={1} />
        </Form.Item>
      </div>
    </div>
  </div>
);

const isEmptyValue = (value: unknown): boolean => value === null || value === undefined || value === '';

const validateSpeedNumber = (value: unknown, fieldLabel: string, required: boolean): Promise<void> => {
  if (isEmptyValue(value)) {
    return required ? Promise.reject(new Error(`${fieldLabel} is required`)) : Promise.resolve();
  }

  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return Promise.reject(new Error(`${fieldLabel} must be a number`));
  if (numericValue < 0) return Promise.reject(new Error(`${fieldLabel} cannot be negative`));
  if (numericValue > 300) return Promise.reject(new Error(`${fieldLabel} cannot be greater than 300`));

  return Promise.resolve();
};

export default CitationRuleEscalationTierConfiguration;
